import {
  createMultiAccountStorage,
  accountUsername,
  type SettingsMap,
  type StorageAdapter,
} from "@apps/client-sdk";
import type { SettingsCache } from "@apps/client-ui";

const DB_NAME = "apps-pwa";
const DB_VERSION = 1;
const STORE = "kv";
const SETTINGS_CACHE_KEY = "apps.settingsCache";

type SettingsCacheMap = Record<string, SettingsMap>;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB open failed"));
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
  });
}

async function idbGet(keys: string[]): Promise<Record<string, unknown>> {
  const db = await openDb();
  const result: Record<string, unknown> = {};
  await Promise.all(
    keys.map(
      (key) =>
        new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE, "readonly");
          const req = tx.objectStore(STORE).get(key);
          req.onsuccess = () => {
            if (req.result !== undefined) {
              result[key] = req.result;
            }
            resolve();
          };
          req.onerror = () => reject(req.error);
        }),
    ),
  );
  return result;
}

async function idbSet(items: Record<string, unknown>): Promise<void> {
  const db = await openDb();
  await Promise.all(
    Object.entries(items).map(
      ([key, value]) =>
        new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE, "readwrite");
          const req = tx.objectStore(STORE).put(value, key);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        }),
    ),
  );
}

async function idbRemove(keys: string[]): Promise<void> {
  const db = await openDb();
  await Promise.all(
    keys.map(
      (key) =>
        new Promise<void>((resolve, reject) => {
          const tx = db.transaction(STORE, "readwrite");
          const req = tx.objectStore(STORE).delete(key);
          req.onsuccess = () => resolve();
          req.onerror = () => reject(req.error);
        }),
    ),
  );
}

const pwaBackend = {
  get: idbGet,
  set: idbSet,
  remove: idbRemove,
};

export function createPwaStorage(): StorageAdapter {
  return createMultiAccountStorage(pwaBackend);
}

export function createPwaSettingsCache(): SettingsCache {
  return {
    async load(account: string): Promise<SettingsMap | null> {
      const key = accountUsername(account);
      const result = await idbGet([SETTINGS_CACHE_KEY]);
      const cache = (result[SETTINGS_CACHE_KEY] as SettingsCacheMap | undefined) ?? {};
      return cache[key] ?? null;
    },
    async save(account: string, settings: SettingsMap): Promise<void> {
      const key = accountUsername(account);
      const result = await idbGet([SETTINGS_CACHE_KEY]);
      const cache = (result[SETTINGS_CACHE_KEY] as SettingsCacheMap | undefined) ?? {};
      cache[key] = settings;
      await idbSet({ [SETTINGS_CACHE_KEY]: cache });
    },
    async clear(account: string): Promise<void> {
      const key = accountUsername(account);
      const result = await idbGet([SETTINGS_CACHE_KEY]);
      const cache = (result[SETTINGS_CACHE_KEY] as SettingsCacheMap | undefined) ?? {};
      delete cache[key];
      if (Object.keys(cache).length === 0) {
        await idbRemove([SETTINGS_CACHE_KEY]);
        return;
      }
      await idbSet({ [SETTINGS_CACHE_KEY]: cache });
    },
  };
}
